// privacy.jsx
import React from 'react';
import { FaInfoCircle, FaBullseye, FaShareAlt, FaLock, FaEnvelope } from 'react-icons/fa';

const Privacy = () => {
  // Smooth scroll function with offset
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // Adjust this based on your navbar height (e.g., 80px for the navbar)
      const sidebarHeight = 60; // Adjust this based on the sticky sidebar's top padding or height
      const offset = navbarHeight + sidebarHeight; // Total offset to account for fixed elements
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 select-none pt-20 pb-24">
      <div className="container mx-auto px-4 py-12 flex flex-col lg:flex-row gap-20">
        {/* Sticky Sidebar Navigation */}
        <aside className="lg:w-1/4 lg:sticky lg:top-20 lg:h-screen">
          <nav className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-maroon mb-4 mt-7">Sections</h2>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => scrollToSection('types')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaInfoCircle /> Types of Information
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('purpose')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaBullseye /> Purpose
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('disclosure')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaShareAlt /> Disclosure
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('security')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaLock /> Security
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaEnvelope /> Contact Us
                </button>
              </li>
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="lg:w-3/4 bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <header className="bg-maroon text-white py-8 px-6 rounded-t-lg shadow-lg animate-fade-in-down">
            <h1 className="text-4xl font-bold tracking-tight">
              Privacy Policy
            </h1>
            <p className="mt-2 text-lg opacity-90">
              Criterionscreeners | Last Updated: April 01, 2025
            </p>
          </header>

          {/* Content Sections */}
          <section className="p-8 space-y-8 text-gray-700 animate-fade-in-up">
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners and Criterionscreeners (in this policy, “we” or “Criterionscreeners”) complies with the Information Technology Act, 2000, Information Technology (Reasonable security practices and procedures and sensitive personal data or information) Rules, 2011 and India DPDP 2023 all other rules, circular and notifications issued thereunder (hereinafter referred to as the ‘Indian Data Protection Law’). The Data Privacy Principles listed in the Guiding Principles apply to all domestic Indian products and services as applicable and to Criterionscreeners entities in India. The following additional principles are specific to Criterionscreeners:
              </p>
            </div>

            <div id="types">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaInfoCircle /> Types of Personal Information Collected
              </h2>
              <p className="text-gray-700 leading-relaxed">
                As part of its services, Criterionscreeners receives information about individuals for the purposes of conducting checks.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Personal Information collected and held about an individual will vary depending upon the background checks required by the Client and the information the individual supplies to us.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Personal Information about an individual that we may collect and hold includes name, age, date of birth, employment history, reference information, education, professional qualifications, residency, sanctions, immigration status, claims, judgments, insolvency, current and previous directorships, character, personal reputation, and such other checks and enquiries as the Client considers necessary to verify information provided by an individual.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                From the sources of our background checks we may collect the name and job title of the person who supplied us with the information.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We may also collect other Personal Information which is “sensitive”. Sensitive personal data or information (“Sensitive Information”) is given a greater level of protection under the Indian Data Protection Law. This Law imposes greater obligations on us regarding any collection, use or disclosure we make of an individual’s Sensitive Information. Sensitive Information about an individual that we may collect and hold includes biometric records and financial information. Please note that any information that is freely available or accessible in the public domain or furnished under the Right to Information Act, 2005 or any other law for the time being in force shall not be regarded as Sensitive Information.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We recognize the importance of privacy issues and respect the confidentiality of the Personal Information and Sensitive Information individuals provide to us. We will collect and deal with such information in accordance with the Indian Data Protection Law, and the data privacy principles and this Privacy Policy (all as amended from time to time).
              </p>
            </div>

            <div id="purpose">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaBullseye /> Purpose of Collection and Usage
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners collects Personal Information that is relevant for the purpose(s) for which it is to be used, consistent with the Indian Data Protection Law. We process Personal Information in ways that are compatible with the purpose(s) for which it has been collected.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                When engaged by a Client, Criterionscreeners verifies an individual’s information, documentation and any responses by carrying out verification checks for the purposes of preparing an employment, investigative, or due diligence report. This is done at the Client’s request to enable them to assess an individual’s suitability for employment, promotion, provision of services, or other purposes in accordance with their legal obligations and internal risk management policy (including but not limited to insurance and claims review).
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                In certain circumstances, Criterionscreeners may be engaged by a Client to prepare an employment or investigative report because they are required by law to determine a candidate’s suitability for a position of employment.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Criterionscreeners may use the Personal Information, documentation and any responses an individual or third party reference has provided to us to apply to various other entities (“Sources”) for verification of an individual’s personal and other information, to collect additional Personal Information, and to conduct public record searches. Examples of Sources include, but are not limited to, government agencies, law enforcement bodies, publicly available records, public registries, court or tribunal records, educational institutions, current and/or previous employers, and regulatory and licensing bodies.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Criterionscreeners will then use the Personal Information to prepare a report outlining the findings of its investigations and provide the report to the Client via a secure means. The Client may then use that information to consider an individual’s suitability for employment, change in the level of responsibility, or other circumstances where an individual’s background is relevant, or to determine if further verification or investigation is necessary.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not use the PII /SPII which we receive from the candidate for any marketing and advertising purpose. Any data used for statistical purposes will be anonymised.
              </p>
            </div>

            <div id="disclosure">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaShareAlt /> Disclosure of Information Collected
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners may disclose Personal Information to clients and third parties acting as agents to complete a check requested. Such disclosure may be necessary for processing purposes to complete the check and will be only for limited and specified purposes.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Criterionscreeners will disclose information only with prior permission from the provider of such information. Client agree to such disclosure in their contracts for completion of checks.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Criterionscreeners may disclose Personal Information to overseas recipients. The circumstances of disclosure may be as follows:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-gray-700 leading-relaxed">
                <li>
                  We may need to disclose an individual’s Personal Information overseas in order to carry out our checks. For example, if an individual lived, studied or worked overseas, we may need to disclose that individual’s Personal Information overseas to liaise with Sources, employers, third party references, or educational institutions. Disclosures of this kind may be to any country in the world, depending upon the circumstances of the individual.
                </li>
                <li>
                  We may also disclose an individual’s Personal Information to our related bodies corporate or agents located overseas. Personal Information may be disclosed to related bodies corporate located overseas for purposes including data storage, administrative purposes (creating internal and external reports, invoicing and trends in data), and operational and/or processing purposes in connection with the preparation of an employment or investigative report to the Client.
                </li>
                <li>
                  We use a limited number of third-party service providers to assist us in providing our services to customers. These third-party providers perform reference and credential verifications and complete portions of services requested by our customers, such as by obtaining records from data sources. These third parties may access or process personal data while providing their services.
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Where required by law, Criterionscreeners may, without consent of the provider, disclose Personal Information to external law enforcement bodies or regulatory authorities, in order to comply with legal obligations.
              </p>
            </div>

            <div id="security">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaLock /> Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners recognizes the importance of securing all Personal Information we collect and process. We adopt reasonable and appropriate security practices and procedures that comply with the requirements of the applicable law. The security practices and procedures implemented are commensurate with the information assets being protected and with the nature of our business and include administrative, physical security, and technical controls to protect and safeguard against the loss, misuse, or unauthorized disclosure, alteration or destruction of Personal Information collected and subject to this policy. Once we receive Personal Information, we take steps to protect its security on our systems. In the event we request or transmit Sensitive Information, we use industry standard, secure socket layer (SSL) encryption. Our Cloud Data Centre, located in the India Region, is certified to meet ISO Standard 27001:2013 & SOC compliant and is regularly audited to ensure that the security controls are effective and in place.
              </p>
            </div>

            <div id="contact">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaEnvelope /> Contact Us/ Grievance
              </h2>
              <p className="text-gray-700 leading-relaxed">
                For redress of discrepancies and grievances with respect to processing of your information in a time bound manner, please contact us:
              </p>
              <p className="text-gray-700 font-medium mt-4">
                <strong>Team Audit and Compliance</strong>
                <br />
                Email:{' '}
                <a
                  href="mailto:audit@criterionbgv.in"
                  className="text-maroon hover:text-maroon-dark underline transition-colors duration-200"
                >
                  audit@criterionbgv.in
                </a>
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Privacy;